import { Box, Divider, Paper, Typography } from "@mui/material";
import { getClientFullName } from "@zfp-akademie-a-s/contract-details";
import { Tag } from "Components/Analyses/_Shared/Tag";
import type { TagColor, TagVariant } from "Components/Analyses/_Shared/Tag";
import { Alert } from "Components/_Shared/Alert";
import { BlSkeleton } from "Components/_Shared/BlSkeleton";
import { Button } from "Components/_Shared/Buttons/Button";
import type { Props as ButtonProps } from "Components/_Shared/Buttons/Button";
import { HStack } from "Components/_Shared/Core/Stack";
import { UserInitialsAvatar } from "Components/_Shared/UserInitialsAvatar";
import { useClientDetail } from "Infrastructure/ApiZfp/AnalyzerApi";
import { RelatedClientDetailType } from "Infrastructure/ApiZfp/Schema";
import { useFormatLocale } from "Infrastructure/Hooks/DateFns/useFormatLocale";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { CLIENT_DETAIL_RETRY, formatClientPhone, selectClientDetailClient } from "Infrastructure/Utils/ClientUtils";

export type ClientCardRowAction = {
  title: string;
  tag?: {
    label: string;
    color: TagColor;
    variant: TagVariant;
  };
  button?: {
    label: string;
    onClick: () => void;
  } & Pick<ButtonProps, "disabled" | "loading" | "color" | "variant">;
};

type Props = {
  clientGuid: string;
  rows: ClientCardRowAction[];
};

const paperSx = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  px: Spacing.XL,
  py: Spacing.L,
  border: "1px solid",
  borderColor: "grey.200",
  borderRadius: 3,
  gap: Spacing.XL,
} as const;

export const ClientCard = ({ clientGuid, rows }: Props) => {
  const { data, isLoading, isError } = useClientDetail(clientGuid, {
    query: { select: selectClientDetailClient, retry: CLIENT_DETAIL_RETRY },
  });
  const formatLocale = useFormatLocale();
  const { t } = useResource();

  if (isLoading) {
    return (
      <Paper elevation={0} sx={paperSx}>
        <HStack spacing={Spacing.L} alignItems="center" minWidth={200}>
          <BlSkeleton variant="circular" width={40} height={40} />
          <Box>
            <BlSkeleton variant="text" width={140} height={20} />
            <BlSkeleton variant="text" width={100} height={16} />
          </Box>
        </HStack>
      </Paper>
    );
  }

  if (isError) {
    return <Alert variant="error" text={t(Resources.Clients.ClientError)} />;
  }

  if (data?.type === RelatedClientDetailType.related) {
    const fullName = getClientFullName({ name: data.firstName, surname: data.surname });
    const birthDate = data.birthDate ? formatLocale(new Date(data.birthDate)) : undefined;
    return (
      <Paper elevation={0} sx={paperSx}>
        <HStack spacing={Spacing.L} alignItems="center" minWidth={200}>
          <UserInitialsAvatar firstName={data.firstName} surname={data.surname} variant="medium" />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {fullName}
            </Typography>
            {birthDate && (
              <Typography variant="caption" color="text.secondary" display="block">
                {birthDate}
              </Typography>
            )}
          </Box>
        </HStack>
      </Paper>
    );
  }

  const fullName = data
    ? getClientFullName({
        name: data.firstName,
        surname: data.surname,
      })
    : "—";

  const phone = formatClientPhone(data?.phonePrefix, data?.phoneNationalNumber);
  const birthDate = data?.birthDate ? formatLocale(new Date(data.birthDate)) : undefined;

  return (
    <Paper elevation={0} sx={paperSx}>
      <HStack spacing={Spacing.L} alignItems="center" minWidth={200}>
        <UserInitialsAvatar firstName={data?.firstName} surname={data?.surname} variant="medium" />
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {fullName}
          </Typography>
          {birthDate && (
            <Typography variant="caption" color="text.secondary" display="block">
              {birthDate}
            </Typography>
          )}
          {phone && (
            <Typography variant="caption" color="text.secondary" display="block">
              {phone}
            </Typography>
          )}
        </Box>
      </HStack>

      <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

      <Box flex={1} minWidth={280} display="flex" flexDirection="column" gap={Spacing.M}>
        {rows.map((row, index) => (
          <HStack key={`${index}-${row.title}`} spacing={Spacing.L} alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Typography variant="body2" fontWeight={500} minWidth={160}>
              {row.title}
            </Typography>
            <HStack spacing={Spacing.L} alignItems="center" flexWrap="wrap">
              {row.tag && <Tag title={row.tag.label} color={row.tag.color} variant={row.tag.variant} />}
              {row.button && (
                <Button
                  onClick={row.button.onClick}
                  disabled={row.button.disabled}
                  loading={row.button.loading}
                  color={row.button.color}
                  variant={row.button.variant}
                >
                  {row.button.label}
                </Button>
              )}
            </HStack>
          </HStack>
        ))}
      </Box>
    </Paper>
  );
};
